import React from 'react';

import {
  PopupMenu,
  TextButton,
  Column,
  Tooltip,
  Icon,
} from 'design-system-web';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { routes } from 'lane-shared/config';
import useChannelAdminContext from 'hooks/useChannelAdminContext';

import { Invoice } from 'lane-shared/domains/billingPayments/types';
import { InvoiceStatusEnum } from 'domains-billing-types';
import { dates } from 'lane-shared/helpers/constants';
import { getInvoiceStatus } from 'lane-shared/helpers/features/getInvoiceStatus';
import { isInvoicePayable } from './isInvoicePayable';
import { dateFormatter } from 'lane-shared/helpers/formatters';
import { formatCurrencyWithSymbolOnly, formatCurrencyNoSymbol } from 'domains-billing-currency';
import {
  currencyToPaymentCurrency,
  graphQLToCurrency,
} from 'domains-billing-web-mappers';
import { Chip, ChipStyle } from 'components/ads';
import { isMonthlyInvoice } from './isMonthlyInvoice';

import { useGetActionsForInvoices } from './useGetActionsForInvoices';
import styles from '../styles.scss';
import useUserLocale from 'hooks/useUserLocale';
import {
  useChargeVerificationPermission,
  useChargesFilteringV2Enabled,
  useSrInvoiceBilledStatusEnabled,
} from 'react-hooks';
import {
  getInvoiceRowActionAvailability,
  MARK_AS_PAID_ACTION_ID,
} from 'domains/billingAndPayments/invoiceBilling/invoiceRowActionAvailability';
import {
  getInvoiceDueDateLabelKey,
  isManagedInvoiceStatus,
} from 'domains/billingAndPayments/invoiceBilling/invoiceStatusPresentation';

export function useGetInvoiceTableColumns(
  timeZone: string,
  threeLetterAbbrev?: string
): Column<Invoice>[] {
  const { t } = useTranslation();
  const isChargesFilteringV2Enabled = useChargesFilteringV2Enabled();
  const isBilledStatusEnabled = useSrInvoiceBilledStatusEnabled();
  const { channel } = useChannelAdminContext();
  const channel_slug = channel?.slug || '';
  const invoiceActions = useGetActionsForInvoices();
  const hasChargeVerificationPermission = useChargeVerificationPermission(
    channel?._id || ''
  );
  const locale = useUserLocale();

  const renderIdCell = (_: any, invoice: Invoice) => {
    const invoiceDisplayRoute = routes.channelAdminInvoiceView
      .replace(':id', channel_slug)
      .replace(':invoiceDisplayId', invoice.displayId);

    const isMonthly = isMonthlyInvoice(invoice);

    return (
      <span className={styles.inlineFlexCenter}>
        <Link to={invoiceDisplayRoute}>{invoice.displayId}</Link>
        {isMonthly && (
          <Tooltip
            TooltipComponent={t('abp.invoiceList.monthlyInvoice')}
            placement="top"
          >
            <span className={styles.calendarIconWrapper}>
              <Icon name="calendar" className={styles.calendarIcon} />
            </span>
          </Tooltip>
        )}
      </span>
    );
  };

  const invoiceColumns: Column<Invoice>[] = [
    {
      key: 'status',
      header: t('abp.invoiceList.status'),
      renderCell: (cell: string, row: Invoice) =>
        renderStatusCell(cell, row, isChargesFilteringV2Enabled),
      renderForCSV: (cell, row) => {
        if (row.status === InvoiceStatusEnum.EXTERNALLY_MANAGED) {
          return isChargesFilteringV2Enabled
            ? t('abp.invoiceList.status.managedExternally')
            : '';
        }
        return t(renderStatusCellForCSV(cell, row));
      },
    },
    {
      key: 'id',
      header: t('abp.invoiceList.invoiceId'),
      renderCell: renderIdCell,
      renderForCSV: (_, row) => row.displayId,
    },
    { key: 'externalPayerId', header: t('abp.invoiceList.customer') },
    {
      key: 'currency',
      header: t('abp.chargeBillingReports.currency'),
      renderCell: renderCurrencyCell,
      renderForCSV: renderCurrencyCellForCSV,
    },
    {
      key: 'amount',
      header: t('abp.invoiceList.amount'),
      renderCell: (cell: number, row: Invoice) => renderAmountCell(cell, row, locale),
      renderForCSV: (cell: number, row: Invoice) => renderAmountCellForCSV(cell, row, locale),
    },
    {
      key: 'invoiceDate',
      header: `${t('abp.invoiceList.issueDate')}${threeLetterAbbrev ? ` ${threeLetterAbbrev}` : ''
        }`,
      renderCell: (cell: string) => renderDateCell(cell, timeZone),
      renderForCSV: (cell: string) => renderDateCellForCSV(cell, timeZone),
    },
    {
      key: 'dueDate',
      header: `${t('abp.invoiceList.dueDate')}${threeLetterAbbrev ? ` ${threeLetterAbbrev}` : ''
        }`,
      renderCell: (cell: string, row: Invoice) => {
        const dueDateLabelKey = getInvoiceDueDateLabelKey(
          row.status,
          isBilledStatusEnabled
        );

        if (dueDateLabelKey) {
          return <span>{t(dueDateLabelKey)}</span>;
        }

        return renderDateCell(cell, timeZone);
      },
      renderForCSV: (cell: string, row: Invoice) => {
        if (isManagedInvoiceStatus(row.status, isBilledStatusEnabled)) {
          return '';
        }

        return renderDateCellForCSV(cell, timeZone);
      },
    },
  ];

  const getRowActionItems = (cell: number, row: Invoice) =>
    invoiceActions.flatMap(({ label, onSelect, testId }) => {
      const { isVisible, disabledReasonKey } = getInvoiceRowActionAvailability({
        actionId: testId,
        status: row.status,
        invoiceDate: row.invoiceDate,
        hasPermission: hasChargeVerificationPermission,
        isBilledStatusEnabled,
      });

      if (!isVisible) return [];

      return [
        {
          label,
          onSelect: () => onSelect(cell, row),
          testId,
          isDisabled:
            !!disabledReasonKey ||
            (testId === MARK_AS_PAID_ACTION_ID && !isInvoicePayable(row)),
          tooltipText: disabledReasonKey ? t(disabledReasonKey) : undefined,
        },
      ];
    });

  if (invoiceActions.length) {
    invoiceColumns.push({
      key: 'actions',
      header: t('abp.actions'),
      renderCell: (cell: number, row: Invoice) =>
        renderActionsCell(t, getRowActionItems(cell, row)),
    });
  }

  return invoiceColumns;
}

function renderStatusCell(cell: string, invoiceDetails: Invoice, isChargesFilteringV2Enabled: boolean) {
  if (cell === InvoiceStatusEnum.EXTERNALLY_MANAGED && !isChargesFilteringV2Enabled) {
    return null;
  }

  const metadata = invoiceDetails.metadata || {};
  const statusMap = getInvoiceStatus(
    cell,
    invoiceDetails,
    metadata.autopay || false
  );
  const chipColor = statusMap[1] as ChipStyle;

  return <Chip value={statusMap[0]} type={chipColor} size="xs" />;
}

function renderStatusCellForCSV(cell: string, invoiceDetails: Invoice): string {
  const statusMap = getInvoiceStatus(cell, invoiceDetails);

  return statusMap[0];
}

function renderAmountCell(cell: number, invoiceDetails: Invoice, locale: string) {
  const currency = graphQLToCurrency(invoiceDetails.currency);

  return (
    <span>
      {formatCurrencyWithSymbolOnly(currency, cell, locale)}
    </span>
  );
}

function renderAmountCellForCSV(cell: number, invoiceDetails: Invoice, locale: string) {
  const currency = graphQLToCurrency(invoiceDetails.currency);

  return formatCurrencyNoSymbol(currency, cell, locale);
}

function renderCurrencyCell(cell: number, invoiceDetails: Invoice) {
  return <span>{currencyToPaymentCurrency(graphQLToCurrency(invoiceDetails.currency))}</span>;
}

function renderCurrencyCellForCSV(cell: number, invoiceDetails: Invoice) {
  return currencyToPaymentCurrency(graphQLToCurrency(invoiceDetails.currency));
}

function renderDateCell(cell: string, timeZone: string) {
  return <span>{dateFormatter(cell, dates.SIMPLE_DATE, timeZone)}</span>;
}

function renderDateCellForCSV(cell: string, timeZone: string) {
  return dateFormatter(cell, dates.SIMPLE_DATE, timeZone);
}

function renderActionsCell(
  t: (key: string) => string,
  items: React.ComponentProps<typeof PopupMenu>['items']
) {
  return (
    <PopupMenu
      trigger={
        <TextButton iconName="angle-down" testId="invoiceActionsButton">
          {t('abp.actions')}
        </TextButton>
      }
      items={items}
    />
  );
}
